import { Injectable } from "@angular/core";
import { Subject, Observable, Subscription } from "rxjs";
import { ColumnTypes } from "./data-grid.component";
import { ElasticSearchService, ES_TYPE } from "../services/elastic-search.service";
import { finalize, map } from "rxjs/operators";
import { ElasticFieldProps, SearchResponse } from "@pienso/ingest-types/elastic.interface";
import { GridDataProvider, Page } from "./grid-data.provider";

@Injectable({ providedIn: "root" })
export class DefaultGridDataProvider implements GridDataProvider {
  private _page$ = new Subject<Page>();

  private _columnTypes$ = new Subject<ColumnTypes>();

  private resultsSubscription: Subscription | null = null;

  constructor(protected elastic: ElasticSearchService) {}

  public get page$(): Observable<Page> {
    return this._page$.asObservable();
  }

  public loadPage(esIndex: string, pageSize: number, pageNumber: number) {
    if (this.resultsSubscription != null) {
      this.resultsSubscription.unsubscribe(); // cancel any in transit requests
      this.resultsSubscription = null;
    }
    // the elastic search offset is based on rows, not pages, so need to convert:
    const esOffset = pageNumber * pageSize;
    const obs = this.getResults(esIndex, pageSize, esOffset);
    this.resultsSubscription = this.pageResults(obs, pageSize, pageNumber)
      .pipe(finalize(() => (this.resultsSubscription = null)))
      .subscribe(
        (page) => this._page$.next(page),
        (error) => this._page$.error(error),
      );
  }

  protected getResults(esIndex: string, pageSize: number, esOffset: number) {
    return this.elastic.searchAll(esIndex, pageSize, esOffset);
  }

  protected pageResults(
    response: Observable<SearchResponse<any>>,
    pageSize: number,
    pageNumber: number,
  ): Observable<Page> {
    // override the pagination button that skips to the last result because Elastic Search really doesn't like that
    // instead skip ahead 20 pages
    const MAX_ROWS = 10000;
    const MAX_PAGENUMBER = Math.ceil(MAX_ROWS / pageSize) - 1;

    return response.pipe(
      map((sr: SearchResponse<any>) => {
        const hits = sr.hits;
        const page = new Page();
        const total = hits.total.value;

        page.totalRows = total;
        page.availableRows = Math.min(total, MAX_ROWS);
        page.pageSize = pageSize; // hits.hits.length;
        page.availablePages = Math.min(Math.ceil(total / pageSize), MAX_PAGENUMBER);
        page.pageNumber = pageNumber;
        page.rows = hits.hits.map((h) => h._source);
        return page;
      }),
    );
  }

  public get columnTypes$() {
    return this._columnTypes$.asObservable();
  }

  public loadColumnTypes(esIndex: string) {
    this.elastic
      .getColumnProperties(esIndex)
      .pipe(
        map((columnProps: ElasticFieldProps) => {
          const types: ColumnTypes = {};
          Object.keys(columnProps).forEach((c) => {
            const type: ES_TYPE = <ES_TYPE>columnProps[c].type;
            const format: string = columnProps[c].format!;
            types[c] = { type };
            if (format) {
              types[c].format = format;
            }
          });
          return types;
        }),
      )
      .subscribe((types) => this._columnTypes$.next(types));
  }
}
